import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Lock, ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ChangePassword = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const isLoggedIn = localStorage.getItem('admin_logged_in');
    if (!isLoggedIn) {
      navigate('/admin/login');
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error('Les contrasenyes noves no coincideixen');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('La contrasenya ha de tenir almenys 6 caràcters');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/admin/change-password`, {
        username: 'admin',
        current_password: currentPassword,
        new_password: newPassword
      });

      toast.success('Contrasenya actualitzada correctament');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      setTimeout(() => {
        navigate('/admin/dashboard');
      }, 1500);
    } catch (error) {
      console.error('Error canviant contrasenya:', error);
      toast.error(error.response?.data?.detail || 'Error canviant la contrasenya');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="change-password-page" className="min-h-screen bg-muted/30 px-6 py-12">
      <div className="max-w-md mx-auto">
        <Link 
          to="/admin/dashboard" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          Tornar al panell
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-card rounded-2xl shadow-sm border border-border/50 p-8"
        >
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-primary/10 text-primary rounded-full flex items-center justify-center">
              <Lock className="h-8 w-8" />
            </div>
          </div>

          <h1 className="font-heading text-3xl font-medium text-center text-foreground mb-2">
            Canviar Contrasenya
          </h1>
          <p className="text-center text-muted-foreground mb-8">
            Actualitza la teva contrasenya d'administrador
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="current" className="text-base mb-2">Contrasenya Actual</Label>
              <Input
                id="current"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                data-testid="current-password"
                className="h-12 rounded-xl"
                required
              />
            </div>

            <div>
              <Label htmlFor="new" className="text-base mb-2">Contrasenya Nova</Label>
              <Input
                id="new"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                data-testid="new-password"
                className="h-12 rounded-xl"
                required
              />
            </div>

            <div>
              <Label htmlFor="confirm" className="text-base mb-2">Confirmar Contrasenya Nova</Label>
              <Input
                id="confirm"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                data-testid="confirm-password"
                className="h-12 rounded-xl"
                required
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="change-password-button"
              className="w-full h-12 text-base font-semibold rounded-xl"
            >
              {loading ? 'Actualitzant...' : 'Canviar Contrasenya'}
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default ChangePassword;